import SectionCurationDetailGroup from 'container/Section/SectionCurationDetailGroup'
import React, { useEffect, useMemo, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import styled, { css } from 'styled-components'
import { getAccessToken, getConfig, getMemberType, getUA } from 'core/singleton'
import { IconSearch, IconCustomSearch } from 'components/Icons'
import { useTranslation } from 'react-i18next'
import {
  SectionBroadcast,
  SectionBroadcastTable,
  SectionContentBanner,
  SectionCuration,
  SectionSchedule,
  SectionTextBanner,
  SectionTopBanner,
} from 'container/Section'
import { STORE_ROOT_PROPS } from 'core/store/global'
import { STORE_CURATION_VISIBLE_SELECTOR } from 'core/store/curation'
import SectionLiveCancellation from 'container/Section/SectionLiveCancellation'
import SectionSauceClip from 'container/Section/SectionSauceClip'
import { GAHelper } from 'core/ga'
import { GA_EVENT } from 'core/ga/enums'
import useCurationDetail from 'core/hooks/useCurationDetail'
import { getLocation, layoutSmall } from 'core/util/default'
import { getExecutorForPartner } from 'core/util/partner'
import { CUSTOM_CLASS_NAME } from 'core/constants'

interface PageMainProps {
  broadcastDisplayType?: 'horizontal' | 'vertical'
  broadcastTitle?: string
}

const PageMain: React.FC<PageMainProps> = (props) => {
  const rootProps = useRecoilValue(STORE_ROOT_PROPS)
  const { logoAltText, customSearchBar, searchBarPlaceholder, searchIconVisible, sauceclip } =
    getConfig()
  const { t } = useTranslation()
  const { isWebview, isSDK } = getUA()

  const isCurationVisible = useRecoilValue(STORE_CURATION_VISIBLE_SELECTOR)
  const [isActiveClips, setIsActiveClips] = useState(false)

  const location = useLocation()

  const token = getAccessToken()
  const memberType = getMemberType()

  const isShowLiveCancellationSection =
    (isWebview || !!getExecutorForPartner(rootProps?.partnerId)?.isIOSWebview) &&
    memberType === '1' &&
    !!token &&
    !!getExecutorForPartner(rootProps?.partnerId)?.isUseLiveCancellation

  const searchBarElement = useMemo(() => {
    if (customSearchBar && searchBarPlaceholder) {
      return (
        <NavLink
          to={`/search${location.search}`}
          className="custom_search_button"
          title={t('search_input')}
          onClick={() => {
            new GAHelper().sendEvent(GA_EVENT.CLICK_SEARCH, {
              partnersId: rootProps?.partnerId,
              location: getLocation(location),
            })
          }}
        >
          <div className="placeholder">{searchBarPlaceholder}</div>
          <IconCustomSearch />
        </NavLink>
      )
    } else if (searchIconVisible) {
      return (
        <div className="search_button">
          <NavLink
            to={`/search${location.search}`}
            title={t('search_input')}
            onClick={() => {
              new GAHelper().sendEvent(GA_EVENT.CLICK_SEARCH, {
                partnersId: rootProps?.partnerId,
                location: getLocation(location),
              })
            }}
          >
            <IconSearch />
          </NavLink>
        </div>
      )
    } else {
      return null
    }
  }, [customSearchBar, searchBarPlaceholder, searchIconVisible, location, t, rootProps?.partnerId])

  /* 소스클립 사용여부 config에서 확인 */
  useEffect(() => {
    if (!sauceclip) return
    const { isSauceClip } = sauceclip
    setIsActiveClips(isSauceClip)
  }, [sauceclip])

  useEffect(() => {
    if (rootProps?.sampleTitle === true) {
      const titleEl = document.getElementById('sf_sample_title')
      if (titleEl) {
        titleEl.style.display = 'inherit'
      }
    }
  }, [rootProps?.sampleTitle])

  /** curation 모듈화 전용 Hook  */
  useCurationDetail(rootProps?.curationId)

  return (
    <MainWrapper>
      {rootProps?.searchBar && (
        <Header className={`${CUSTOM_CLASS_NAME.HEADER}`}>
          <div className={`partner_logo ${CUSTOM_CLASS_NAME.HEADER_PARTNER_LOGO}`}>
            <LogoImg alt={logoAltText} tabIndex={logoAltText ? 0 : -1} />
          </div>
          {searchBarElement}
        </Header>
      )}
      <div className={`${CUSTOM_CLASS_NAME.CONTENTS}`}>
        {rootProps?.topBanner && <SectionTopBanner />}
        {rootProps?.contentBanner && <SectionContentBanner />}
        {rootProps?.textBanner && <SectionTextBanner />}
        {rootProps?.broadcast && (
          <SectionBroadcast displayType={props.broadcastDisplayType} title={props.broadcastTitle} />
        )}
        {rootProps?.schedule && <SectionSchedule type={rootProps?.scheduleType} />}
        {rootProps?.sauceClip && !isSDK && sauceclip?.isSauceClip && isActiveClips && (
          <SectionSauceClip />
        )}
        {rootProps?.broadcastTable && <SectionBroadcastTable />}
        <SectionCuration isVisible={Boolean(rootProps?.curation && isCurationVisible)} />
        <SectionCurationDetailGroup
          isVisible={Boolean(rootProps?.curationDetail && isCurationVisible)}
        />
        {isShowLiveCancellationSection && <SectionLiveCancellation />}
      </div>
    </MainWrapper>
  )
}

const MainWrapper = styled.div`
  overflow-x: hidden;
`

const Header = styled.div`
  height: 50px;
  padding: 0 20px;

  display: flex;
  align-items: center;

  .partner_logo > img {
    display: ${({ theme }) => (theme.logoUse ? `block;` : `none;`)};
    width: 150px;
    height: 50px;
  }
  .partner_logo > .text {
    font-weight: 700;
    font-size: 20px;
    line-height: 1.2;
  }
  .search_button {
    width: 48px;
    height: 48px;
    margin-left: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${(props) => props.theme.icon};
    cursor: pointer;
  }

  .custom_search_button {
    max-width: 60%;
    height: 34px;
    border-radius: 22px;
    margin-left: auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: ${(props) => props.theme.icon};
    border: solid 1px ${(props) => props.theme.icon};
    background-color: transparent;
    cursor: pointer;
    padding: 0px 10px;
    @media (max-width: 360px) {
      max-width: 53%;
    }

    .placeholder {
      padding: 0 10px 0 16px;
      font-size: 14px;
      font-weight: 500;
      color: ${(props) => props.theme.icon};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  ${layoutSmall(css`
    padding: 0 16px;
  `)}
`

const LogoImg = styled.img.attrs((props) => ({
  src: props.theme.logo && props.theme.logoUse ? props.theme.logo : '',
}))``
export default PageMain
